import React, { useEffect, useState } from 'react';

import { Button, Collapse, Form, Input, Space, Typography, Select } from 'antd';
import { Store } from 'antd/es/form/interface';
import { CaretRightOutlined, PlusOutlined } from '@ant-design/icons';

import {
  VALIDATION_ERROR_MESSAGES,
  PLATFORM_PUBLICATION,
  TARGET,
  MR_PLATFORM,
  ENSEMBL_PLATFORM,
} from '../../../../constants';
import { MediaUploader } from '../../../../ui';
import TownsTags from '../../../../ui/Tags/TownsTags';
import DistrictsTags from '../../../../ui/Tags/DistrictsTags';

import { ApiPublicationType } from '../../types';
import { UploadedMediaType } from '../../../../ui/MediaUploader/types';

import styles from '../../Publications.module.css';
import { connect } from 'react-redux';
import { RootState } from 'src/reducers/store';

type FromViewProps = {
  onSubmit: (rule: any) => void;
  loading: boolean;
  publication?: ApiPublicationType;
  isMine?: boolean;
  allTowns: Array<{ id: string; name: string }>;
};

const { Panel } = Collapse;
const { Title } = Typography;
const { Option } = Select;

const FormView: React.FC<FromViewProps> = ({
  onSubmit,
  loading,
  publication,
  isMine = true,
  allTowns,
}) => {
  const [form] = Form.useForm();
  let initialValues;

  if (publication) {
    initialValues = {
      title: publication.title,
      text: publication.text,
      medias: publication.medias.map(media => ({
        id: media.id,
        name: media.name,
        status: 'done',
        url: media.url.original,
        type: media.mime,
      })),
      platform: publication.target_platforms.includes(
        ...PLATFORM_PUBLICATION.maResidence
      )
        ? MR_PLATFORM
        : ENSEMBL_PLATFORM,
      target: publication.target_platforms.includes(
        ...PLATFORM_PUBLICATION.maResidence
      )
        ? TARGET.national
        : TARGET.city,
      targetDistricts: publication.target_districts || [],
      targetTowns: publication?.target_platforms.includes(
        ...PLATFORM_PUBLICATION.maResidence
      )
        ? []
        : publication.target_towns.length
        ? publication.target_towns.map(townName =>
            allTowns.find(({ name }) => {
              return name === townName;
            })
          )
        : publication.diffusions.map(({ space }) =>
            allTowns.find(({ id }) => {
              return id === space.id;
            })
          ),
    };
  } else {
    initialValues = {
      title: '',
      text: '',
      medias: [],
      platform: null,
      target: null,
      targetDistricts: [],
      targetTowns: [],
    };
  }

  const [title] = useState(
    publication ? 'Modifier ma publication' : 'Créer une publication'
  );
  const [disableInput] = useState(!isMine);
  const [platform, setPlatform] = useState(initialValues.platform);
  const [target, setTarget] = useState(initialValues.target);
  const [towns, setTowns] = useState(initialValues.targetTowns);

  const requiredValidationRule = {
    required: true,
    message: VALIDATION_ERROR_MESSAGES.required,
  };

  const submitForm = async (values: Store) => {
    const dataToPost = {
      title: values.title,
      text: values.text,
      label: 'information',
      medias: values.medias.map((media: UploadedMediaType) => ({
        id: media.id,
      })),
      publisher: {
        id: process.env.REACT_APP_AUTOMATION_PUBLISHER_ID,
        model_type: 'user',
      },
      for_free: true,
    };

    if (values.platform === MR_PLATFORM) {
      dataToPost.target_platforms = PLATFORM_PUBLICATION[values.platform];
    }

    if (
      values.platform === ENSEMBL_PLATFORM &&
      values.target === TARGET.city &&
      towns.length === 1 &&
      values.targetDistricts.length
    ) {
      dataToPost.target_towns = towns.map(({ name }) => name);
      dataToPost.target_districts = values.targetDistricts;
      dataToPost.target_platforms = PLATFORM_PUBLICATION[values.platform];
    }

    if (
      values.platform === ENSEMBL_PLATFORM &&
      values.target === TARGET.city &&
      towns.length >= 1 &&
      (!values.targetDistricts || !values.targetDistricts?.length)
    ) {
      dataToPost.diffusions = towns.map(({ id }) => ({
        space: { id: id, model_type: 'place' },
      }));
    }

    onSubmit(dataToPost);
  };

  useEffect(() => {
    !loading && form.resetFields();
  }, [loading, form]);

  return (
    <Form
      id="messageForm"
      form={form}
      onFinish={submitForm}
      initialValues={initialValues}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Title level={3} style={{ marginTop: '5px' }}>
          {title}
        </Title>
        <Form.Item name="title" rules={[requiredValidationRule]}>
          <Input
            placeholder="Titre de publication"
            id="title"
            disabled={disableInput}
          />
        </Form.Item>
        <Collapse
          bordered={false}
          expandIconPosition="right"
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Panel
            id="descriptionPanel"
            forceRender={true}
            header={
              <>
                <div className={styles.circle}>1</div>
                <span>Rédigez votre message</span>
              </>
            }
            key="text"
          >
            <Form.Item
              name="text"
              rules={[
                {
                  required: true,
                  message: VALIDATION_ERROR_MESSAGES.required,
                },
                {
                  max: 400,
                  message: VALIDATION_ERROR_MESSAGES.len,
                },
              ]}
            >
              <Input.TextArea
                id="message"
                placeholder="Message ( maximum 400 caractères)"
                disabled={disableInput}
              />
            </Form.Item>
            <Form.Item key="medias" name="medias">
              <MediaUploader
                limit={1}
                placeholder="Télécharger des photos ou des documents"
                pictureListType="picture-card"
                allExtensions={true}
                defaultValue={initialValues.medias}
                disabled={disableInput}
                canRemove
                onUpload={(medias: Array<UploadedMediaType>) => {
                  form.setFieldsValue({
                    medias,
                  });
                }}
              >
                <Button id="media" icon={<PlusOutlined />}></Button>
              </MediaUploader>
            </Form.Item>
          </Panel>
          <Panel
            id="targetPanel"
            forceRender={true}
            header={
              <>
                <div className={styles.circle}>2</div>
                <span>Définissez votre ciblage</span>
              </>
            }
            key="target"
          >
            <Form.Item name="platform" rules={[requiredValidationRule]}>
              <Select
                id="platform"
                placeholder="Platforme"
                disabled={disableInput}
                onChange={(platform: string) => {
                  setPlatform(platform);
                  form.setFieldsValue({
                    target: null,
                  });
                  setTarget(null);
                }}
                style={{ width: '30%' }}
              >
                <Option id="ensembl" value={ENSEMBL_PLATFORM}>
                  Ensembl
                </Option>
                <Option id="maResidence" value={MR_PLATFORM}>
                  Ma-residence
                </Option>
              </Select>
            </Form.Item>
            <Form.Item name="target" rules={[requiredValidationRule]}>
              <Select
                id="target"
                placeholder="Ciblage"
                disabled={disableInput}
                onChange={(target: string) => {
                  form.setFieldsValue({
                    target,
                  });
                  setTarget(target);
                }}
                style={{ width: '210px' }}
              >
                {platform === MR_PLATFORM && (
                  <Option id="national" value={TARGET.national}>
                    Tous les utilisateurs MR
                  </Option>
                )}
                {platform === ENSEMBL_PLATFORM && (
                  <Option id="city" value={TARGET.city}>
                    Ville
                  </Option>
                )}
              </Select>
            </Form.Item>
            {target === TARGET.city && (
              <>
                <Form.Item name="targetTowns" rules={[requiredValidationRule]}>
                  <TownsTags
                    defaultValue={initialValues.targetTowns?.map(
                      town => town?.name
                    )}
                    disabled={disableInput}
                    onSelectChange={(targetTowns: Array<string>) => {
                      form.setFieldsValue({
                        targetTowns,
                      });
                      setTowns(targetTowns);
                    }}
                  />
                </Form.Item>
                {towns.length === 1 && (
                  <Form.Item name="targetDistricts">
                    <DistrictsTags
                      town={towns[0]?.name}
                      disabled={disableInput}
                      defaultValue={initialValues.targetDistricts}
                      onSelectChange={(targetDistricts: Array<string>) =>
                        form.setFieldsValue({
                          targetDistricts,
                        })
                      }
                    />
                  </Form.Item>
                )}
              </>
            )}
          </Panel>
        </Collapse>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            id="submit"
            htmlType="submit"
            type="primary"
            disabled={disableInput}
            loading={loading}
          >
            Publier
          </Button>
        </div>
      </Space>
    </Form>
  );
};

const mapStateToProps = ({ towns }: RootState) => ({ allTowns: towns });

export default connect(mapStateToProps)(FormView);
